import { quickAddActionCreators } from '@app/components/QuickAddModal/modules/quickAdd'
import {
  AssociatedContact,
  IAssociatedContact,
} from '@app/models/AssociatedContact'
import { IQuickContact } from '@app/models/QuickContact'
import {
  actionCreators,
  IGlobalCompanyContactsState,
  IGlobalHbUserState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { List } from 'immutable'
import * as React from 'react'
import { MultiValue } from 'react-select'
import Creatable from 'react-select/creatable'
import { v4 as uuid } from 'uuid'

interface IAssociatedContactsSelectorProps {
  className?: string
  id?: string
  name?: string
  onChange: (newValue: List<AssociatedContact>) => void
  placeholder?: string
  required?: boolean
  usersOnly?: boolean
  value: List<IAssociatedContact>
}

type AssociatedContactsSelectorProps = IAssociatedContactsSelectorProps &
  IGlobalCompanyContactsState &
  IGlobalHbUserState &
  typeof actionCreators &
  typeof quickAddActionCreators

export class AssociatedContactsSelector extends React.PureComponent<
  AssociatedContactsSelectorProps
> {
  private selectRef: React.RefObject<HTMLDivElement>
  private instanceId: string

  constructor(props: AssociatedContactsSelectorProps) {
    super(props)
    this.selectRef = React.createRef()
    this.instanceId = props.id || uuid()
    bindFunctions(this)
  }

  public componentDidMount() {
    if (this.props.usersOnly) {
      this.props.getHbcontacts()
    } else {
      this.props.getCompanyContactsList()
    }
    this._checkValidity()
  }

  public componentDidUpdate() {
    this._checkValidity()
  }

  public render() {
    return (
      <div className={this.props.className} ref={this.selectRef}>
        <Creatable
          getOptionLabel={(opt: any) => 'label' in opt ? opt.label : opt.name} // opt.label is the value of formatCreateLabel
          getOptionValue={(opt: any) => 'label' in opt ? opt.label : opt.contactId}
          isMulti
          isLoading={this._contacts().count() === 0}
          formatCreateLabel={this._generateCreatePromptText}
          onChange={this._onChange}
          onCreateOption={this._onCreateOption}
          options={
            this._contacts().filter((contact) => (contact?.name.length ?? 0) > 0).toArray()
          }
          placeholder={this.props.placeholder || 'Add connections...'}
          required={this.props.required}
          value={this.props.value.toArray()}
        />
      </div>
    )
  }

  private _contacts(): List<IAssociatedContact> {
    return this.props.usersOnly ? this.props.hbContacts : this.props.companyContacts.results
  }

  private _checkValidity() {
    if (this.selectRef.current) {
      const inputElem = this.selectRef.current.querySelector('input:invalid')
      if (inputElem) {
        this.selectRef.current.classList.add('invalid')
      } else {
        this.selectRef.current.classList.remove('invalid')
      }
    }
  }

  private _generateCreatePromptText(filterText: string): string {
    return `Create a new connection for "${filterText}"`
  }

  private _isOptionUnique({
    labelKey,
    option,
    options,
  }: {
    labelKey: string
    option: any
    options: any[]
  }): boolean {
    if (!options || !options.length) {
      return true
    }

    const testValue = (option[labelKey] as string).toLowerCase()

    for (const opt of options) {
      const optValue = (opt[labelKey] as string).toLowerCase()
      if (optValue.indexOf(testValue) >= 0) {
        return false
      }
    }

    return true
  }

  private _onChange(newValue: MultiValue<IAssociatedContact>) {
    const contactsArray = newValue.map((a) =>
      AssociatedContact.fromJS(a as IAssociatedContact),
    )

    this.props.onChange(List(contactsArray))
  }

  private _onCreateOption(name: string) {
    this.props.showQuickAdd(
      this.instanceId,
      name,
      this._onQuickAddSave,
    )
  }

  private _onQuickAddSave(contact: IQuickContact) {
    const newContact = new AssociatedContact({
      contactId: contact.contactId,
      isCompany: contact.isCompany,
      name: contact.isCompany
        ? contact.lastName
        : `${contact.firstName} ${contact.lastName}`,
    })
    this.props.onChange(
      this.props.value.push(newContact) as List<AssociatedContact>,
    )
  }
}
